import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from "common";
import { colors } from "../components/Theme/WebTheme";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import CircularLoading from "../components/CircularLoading";
import AlertDialog from "../components/AlertDialog";
import GoBackButton from "components/GoBackButton";

// Imagem com zoom (roda do mouse / botões) e arraste para mover
const ZoomableImage = ({ src, label }) => {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const clamp = (s) => Math.min(5, Math.max(1, s));

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((prev) => {
        const next = clamp(prev - e.deltaY * 0.0015);
        if (next === 1) setPos({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onMouseDown = (e) => {
    if (scale === 1) return;
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const endDrag = () => {
    dragging.current = false;
  };
  const zoomIn = () => setScale((s) => clamp(s + 0.5));
  const zoomOut = () =>
    setScale((s) => {
      const n = clamp(s - 0.5);
      if (n === 1) setPos({ x: 0, y: 0 });
      return n;
    });
  const reset = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };
  const onDoubleClick = () => (scale === 1 ? setScale(2.5) : reset());

  const btnStyle = { backgroundColor: "rgba(255,255,255,0.9)" };

  return (
    <div>
      <Typography
        align="center"
        style={{
          fontWeight: "bold",
          fontFamily: FONT_FAMILY,
          marginBottom: 8,
          color: colors.Header_Text_back,
        }}
      >
        {label}
      </Typography>
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={onDoubleClick}
        style={{
          position: "relative",
          width: "100%",
          height: 380,
          overflow: "hidden",
          borderRadius: 12,
          background: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: scale > 1 ? "grab" : "default",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
        }}
      >
        {src ? (
          <img
            src={src}
            alt={label}
            draggable={false}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
              transition: dragging.current ? "none" : "transform 0.05s",
              userSelect: "none",
            }}
          />
        ) : (
          <Typography style={{ color: "#fff", fontFamily: FONT_FAMILY }}>
            Sem imagem
          </Typography>
        )}
        {src ? (
          <div
            style={{
              position: "absolute",
              bottom: 8,
              right: 8,
              display: "flex",
              gap: 4,
            }}
          >
            <IconButton size="small" onClick={zoomOut} style={btnStyle}>
              <ZoomOutIcon />
            </IconButton>
            <IconButton size="small" onClick={zoomIn} style={btnStyle}>
              <ZoomInIcon />
            </IconButton>
            <IconButton size="small" onClick={reset} style={btnStyle}>
              <RefreshIcon />
            </IconButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const statusInfo = (status) => {
  switch (status) {
    case "approved":
      return { label: "Aprovada", color: colors.GREEN };
    case "rejected":
      return { label: "Recusada", color: colors.RED };
    case "pending":
      return { label: "Pendente", color: "#f57c00" };
    default:
      return { label: "Sem envio", color: colors.WALLET_CARD_SHADOW };
  }
};

// Data/hora da última mudança de status facial, rotulada conforme o status atual.
const statusDateLabel = (status) => {
  switch (status) {
    case "approved":
      return "Aprovada em";
    case "rejected":
      return "Atualizada em";
    default:
      return "Enviada em";
  }
};

// Motivo pré-preenchido ao revogar uma aprovação (o admin pode editar antes de confirmar).
const DEFAULT_REVOKE_REASON =
  "Sua verificação foi revogada. Por favor, envie uma nova selfie para revalidar sua identidade.";

export default function FacialValidation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const { fetchUsersOnce, setFacialStatus } = api;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState("");
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      const u = staticusers.find((x) => x.id === id.toString());
      if (!u) {
        navigate("/404");
      } else {
        setUser(u);
      }
      setLoading(false);
    }
  }, [staticusers, id, navigate]);

  const handleApprove = () => {
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    dispatch(setFacialStatus(user.id, "approved"));
    setTimeout(() => navigate("/facialvalidations"), 600);
  };

  const handleConfirmReject = () => {
    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    if (!reason.trim()) return;
    dispatch(setFacialStatus(user.id, "rejected", reason.trim()));
    setRejectOpen(false);
    setRevoking(false);
    setTimeout(() => navigate("/facialvalidations"), 600);
  };

  const closeReasonDialog = () => {
    setRejectOpen(false);
    setRevoking(false);
  };

  if (loading || !user) return <CircularLoading />;

  const info = statusInfo(user.facialStatus);
  const isApproved = user.facialStatus === "approved";

  return (
    <Card
      style={{
        borderRadius: "19px",
        backgroundColor: colors.WHITE,
        marginTop: 10,
        marginBottom: 10,
        padding: 16,
        boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: isRTL === "rtl" ? "flex-end" : "flex-start",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            margin: "8px 8px 0 4px",
          }}
        >
          <Typography variant="h5" style={{ fontFamily: FONT_FAMILY }}>
            {`${user.firstName || ""} ${user.lastName || ""}`}
          </Typography>
          <Chip
            label={info.label}
            style={{
              backgroundColor: info.color,
              color: colors.WHITE,
              fontFamily: FONT_FAMILY,
              fontWeight: "bold",
            }}
          />
          {user.facialUpdatedAt ? (
            <Typography
              style={{
                fontFamily: FONT_FAMILY,
                color: colors.Header_Text_back,
                fontSize: 13,
              }}
            >
              {`${statusDateLabel(user.facialStatus)} ${new Date(
                user.facialUpdatedAt
              ).toLocaleString()}`}
            </Typography>
          ) : null}
        </div>
        <GoBackButton isRTL={isRTL} onClick={() => navigate("/facialvalidations")} />
      </div>

      <Typography
        align="center"
        style={{
          fontFamily: FONT_FAMILY,
          color: colors.Header_Text_back,
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        Use a roda do mouse ou os botões para dar zoom. Arraste a imagem para mover. Clique duas vezes para aproximar/afastar.
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={4}>
          <ZoomableImage src={user.facialImage} label="Selfie" />
        </Grid>
        <Grid item xs={12} md={4}>
          <ZoomableImage src={user.verifyIdImage} label="Id / Passaporte" />
        </Grid>
        <Grid item xs={12} md={4}>
          <ZoomableImage src={user.licenseImage} label="CNH" />
        </Grid>
      </Grid>

      {user.facialStatus === "rejected" && user.facialRejectionReason ? (
        <Typography
          align="center"
          style={{
            fontFamily: FONT_FAMILY,
            color: colors.RED,
            marginTop: 16,
          }}
        >
          {`Motivo da recusa anterior: ${user.facialRejectionReason}`}
        </Typography>
      ) : null}

      <Grid
        container
        spacing={2}
        justifyContent="center"
        style={{ marginTop: 8 }}
      >
        {isApproved ? (
          // Já aprovada: única ação é revogar e pedir uma nova selfie
          // (reusa o fluxo de recusa, que re-arma o gate e notifica o usuário).
          <Grid item xs={12} sm={8} md={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ReplayIcon />}
              onClick={() => {
                setReason(DEFAULT_REVOKE_REASON);
                setRevoking(true);
                setRejectOpen(true);
              }}
              style={{
                backgroundColor: "#f57c00",
                borderRadius: "19px",
                minHeight: 50,
                color: colors.WHITE,
                fontFamily: FONT_FAMILY,
                fontWeight: "bold",
              }}
            >
              Revogar e solicitar nova
            </Button>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleApprove}
                style={{
                  backgroundColor: colors.GREEN,
                  borderRadius: "19px",
                  minHeight: 50,
                  color: colors.WHITE,
                  fontFamily: FONT_FAMILY,
                  fontWeight: "bold",
                }}
              >
                Aprovar
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setReason("");
                  setRevoking(false);
                  setRejectOpen(true);
                }}
                style={{
                  backgroundColor: colors.RED,
                  borderRadius: "19px",
                  minHeight: 50,
                  color: colors.WHITE,
                  fontFamily: FONT_FAMILY,
                  fontWeight: "bold",
                }}
              >
                Recusar
              </Button>
            </Grid>
          </>
        )}
      </Grid>

      <Dialog
        open={rejectOpen}
        onClose={closeReasonDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle style={{ fontFamily: FONT_FAMILY }}>
          {revoking ? "Revogar aprovação" : "Motivo da recusa"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            margin="dense"
            placeholder={
              revoking
                ? "Explique por que a verificação está sendo revogada. O usuário verá esta mensagem e deverá enviar uma nova selfie."
                : "Descreva por que a selfie foi recusada (ex.: rosto não corresponde ao documento, foto borrada, com máscara...)"
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            InputProps={{ style: { fontFamily: FONT_FAMILY } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeReasonDialog}
            style={{ color: colors.Header_Text_back, fontFamily: FONT_FAMILY }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmReject}
            disabled={!reason.trim()}
            variant="contained"
            style={{
              backgroundColor: reason.trim() ? colors.RED : colors.WALLET_CARD_SHADOW,
              color: colors.WHITE,
              fontFamily: FONT_FAMILY,
            }}
          >
            {revoking ? "Revogar" : "Confirmar recusa"}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={commonAlert.open}
        onClose={() => setCommonAlert({ open: false, msg: "" })}
      >
        {commonAlert.msg}
      </AlertDialog>
    </Card>
  );
}
